import React, { useState } from 'react';

import i18n from 'i18n-js';
import clsx from 'clsx';
import {
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import { ListItemText } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import PrintIcon from '@material-ui/icons/Print';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import {
    BasicThreeDotsMenu,
    SectionTab,
    PopupConfirmation,
    Text,
    ColorSelector,
} from 'components/shared';
import { CategoryDetailsData, CategoryDishData, PrintConfig } from 'store/categoryStore/reducer';
import styles from '../styles';
import CreateOrEditCategoryModal from './CreateOrEditCategoryModal';
import PaletteIcon from '@material-ui/icons/Palette';
import DeleteIcon from '@material-ui/icons/Delete';
import SetCategoryPrinterConfigurations from './SetCategoryPrinterConfigurations';

class Category {
    public id: string;
    public name: string;
    public position: string;
    public isDisabled: boolean;
    public color: string;
    public printerConfigurations?: PrintConfig[];
    constructor(category?: Category) {
        this.id = category?.id ?? '';
        this.name = category?.name ?? '';
        this.position = category?.position ?? '';
        this.isDisabled = category?.isDisabled ?? false;
        this.color = category?.color ?? '';
        this.printerConfigurations = category?.printerConfigurations ?? [];
    }
}

interface CategoriesProps {
    categoryDetails: CategoryDetailsData;
    onCreate: (categoryName: string, dishes: string[], position: string) => void;
    onEdit: (categoryName: string, dishes: string[], position: string, categoryId: string) => void;
    onDisableOrEnable: (categoriesIds: string[]) => void;
    onDelete: (categoryId: string) => void;
    onOrder: (id: string) => void;
    orderBy: string;
    orderNames: string[];
    asc: boolean;
    onSaveColor: (id: string, value: string) => void;
}

export default function Categories(props: CategoriesProps) {
    const dispatch = useDispatch();
    const [localCategoryDetails, setLocalCategoryDetails] = useState<Category>(new Category());
    const [editDishes, setEditDishes] = useState<CategoryDishData[]>([]);
    const [showPopupConfirmation, setShowPopupConfirmation] = useState(0);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryToPrinterConfig, setCategoryToPrinterConfig] = useState<Category | null>(null);
    const classes = styles();
    const [showColorSelector, setShowColorSelector] = useState(false);
    const [color, setColor] = useState<string>(localCategoryDetails.color);

    function getListMenuItems(category: Category) {
        return [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => editCategory(category)}
            >
                <EditIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('common.edit')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleChangeColorClick(category)}
            >
                <PaletteIcon
                    fontSize="small"
                    color="inherit"
                    className={classes.threeDotsItemIcon}
                />
                <ListItemText primary={i18n.t('common.changeColor')} />
            </div>,
            /* <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => setCategoryToPrinterConfig(category)}
            >
                <PrintIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('common.printerConfigurations')} />
            </div>, */
            category.isDisabled ? (
                <div
                    className={classes.threeDotsMenuItemContainer}
                    onClick={() => showDisableOrEnablePopup(category.id, category.isDisabled)}
                >
                    <SettingsBackupRestoreIcon className={classes.threeDotsItemIcon} />
                    <ListItemText primary={i18n.t('button.restore')} />
                </div>
            ) : (
                <div
                    className={classes.threeDotsMenuItemContainer}
                    onClick={() => showDisableOrEnablePopup(category.id, category.isDisabled)}
                >
                    <RemoveCircleOutlineIcon className={classes.threeDotsItemIcon} />
                    <ListItemText primary={i18n.t('common.disable')} />
                </div>
            ),

            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => showDeletePopup(category)}
            >
                <DeleteIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={'Delete'} />
            </div>,
        ];
    }

    function showDisableOrEnablePopup(id: string, isDisabled: boolean) {
        setLocalCategoryDetails({ ...localCategoryDetails, id });
        setShowPopupConfirmation(isDisabled ? 1 : 2);
    }

    function showDeletePopup(category: Category) {
        if (getCategoryDishes(category.id).length > 0) {
            dispatch(
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('common.wantDeleteWorkAreaWithDishesError')
                )
            );
            return;
        }
        setLocalCategoryDetails(category);
        setShowDeleteConfirmation(true);
    }

    function createCategory() {
        displayCategoryModal();
        setEditDishes([]);
        setLocalCategoryDetails(new Category());
        setIsEdit(false);
    }

    function editCategory(category: Category) {
        setEditDishes(getCategoryDishes(category.id));
        setLocalCategoryDetails(category);
        setIsEdit(true);
        displayCategoryModal();
    }

    function displayCategoryModal() {
        setShowCategoryModal(true);
    }

    function hideCategoryModal() {
        setShowCategoryModal(false);
    }

    function getCategoryDishes(categoryId: string) {
        let { dishes } = props.categoryDetails;
        dishes = dishes.filter((dish) => dish.categoryId === categoryId);
        return dishes;
    }

    function handleChangeColorClick(category: Category) {
        setLocalCategoryDetails(category);
        setShowColorSelector(true);
    }

    function handleColorChange(value: string) {
        setColor(value);
    }

    function handleSaveColor() {
        props.onSaveColor(localCategoryDetails.id, color);
        handleColorReset();
    }

    function handleColorReset() {
        setShowColorSelector(false);
        setColor('');
    }

    return (
        <div className={classes.categoriesContainer}>
            <CreateOrEditCategoryModal
                categoryDetails={props.categoryDetails}
                isVisible={showCategoryModal}
                onClose={hideCategoryModal}
                onCreate={props.onCreate}
                onEdit={props.onEdit}
                position={localCategoryDetails.position}
                categoryName={localCategoryDetails.name}
                categoryId={localCategoryDetails.id}
                dishes={editDishes}
                isEdit={isEdit}
                categories={props.categoryDetails.categories.reduce((result: string[], current) => {
                    if (localCategoryDetails.name !== current.name) {
                        result.push(current.name.toUpperCase());
                    }
                    return result;
                }, [])}
            />

            <Table className={classes.table} aria-label="simple table">
                <TableHead>
                    <TableRow key="table-head">
                        <TableCell>
                            <TableSortLabel
                                active={props.orderBy === props.orderNames[0]}
                                direction={props.asc ? 'asc' : 'desc'}
                                onClick={() => props.onOrder(props.orderNames[0])}
                            >
                                <span className={classes.tableHead}>
                                    {i18n.t('restaurant.workAreas')}
                                </span>
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={props.orderBy === props.orderNames[1]}
                                direction={props.asc ? 'asc' : 'desc'}
                                onClick={() => props.onOrder(props.orderNames[1])}
                            >
                                <span className={classes.tableHead}>
                                    {i18n.t('common.numberOfDishes')}
                                </span>
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <span className={classes.tableHead}>{i18n.t('common.color')}</span>
                        </TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={6} padding="none">
                            <SectionTab
                                text={i18n.t('restaurant.newWorkArea')}
                                onClick={createCategory}
                            />
                        </TableCell>
                    </TableRow>
                    {props.categoryDetails.categories.map((category) => (
                        <TableRow key={category.id}>
                            <TableCell
                                className={`${category.isDisabled && classes.disabledOpacity}`}
                            >
                                <Text text={category.name} maxLength={35} />
                            </TableCell>
                            <TableCell
                                className={`${category.isDisabled && classes.disabledOpacity}`}
                            >
                                {getCategoryDishes(category.id).length}
                            </TableCell>
                            <TableCell
                                width={50}
                                className={`${category.isDisabled && classes.disabledOpacity}`}
                            >
                                <Chip
                                    size="small"
                                    style={{ width: '100%', backgroundColor: category.color }}
                                />
                            </TableCell>
                            <TableCell
                                width={50}
                                align="right"
                                className={clsx({
                                    [classes.threeDotsMenuDisabledCell]: category.isDisabled,
                                })}
                            >
                                <BasicThreeDotsMenu items={getListMenuItems(category)} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <PopupConfirmation
                open={showPopupConfirmation > 0}
                close={() => setShowPopupConfirmation(0)}
                title={
                    showPopupConfirmation === 1
                        ? i18n.t('button.restore')
                        : i18n.t('common.disable')
                }
                description={
                    showPopupConfirmation === 1
                        ? i18n.t('confirmation.sureToRestoreWorkArea')
                        : i18n.t('confirmation.sureToDisableWorkArea')
                }
                activeBtn={
                    showPopupConfirmation === 1
                        ? i18n.t('button.restore')
                        : i18n.t('common.disable')
                }
                action={() => props.onDisableOrEnable([localCategoryDetails.id])}
            />
            <PopupConfirmation
                open={showDeleteConfirmation}
                close={() => setShowDeleteConfirmation(false)}
                title={i18n.t('common.wantDeleteWorkArea')}
                description={''}
                activeBtn={i18n.t('common.delete')}
                action={() => props.onDelete(localCategoryDetails.id)}
            />
            <ColorSelector
                isOpen={showColorSelector}
                onClose={() => setShowColorSelector(false)}
                value={color}
                onChange={handleColorChange}
                onSave={handleSaveColor}
                onCancel={handleColorReset}
            />
            <SetCategoryPrinterConfigurations
                open={!!categoryToPrinterConfig}
                categoryId={categoryToPrinterConfig?.id}
                initialPrintConfigs={categoryToPrinterConfig?.printerConfigurations || []}
                onClose={() => setCategoryToPrinterConfig(null)}
            />
        </div>
    );
}
